/**
 * Visible questions/options helpers — filters API question lists by conditional expressions.
 *
 * Belongs here: getVisibleQuestions, findNextVisibleQuestionIndex, option filtering.
 * Does not belong here: json-logic parsing (see evaluateCondition.ts).
 */
import {
  StrategyQuestionnaireStage,
  Question,
  QuestionOption,
  StrategyQuestionnaireAnswersMap
} from '../shared/types/questionnaire.types'
import { evaluateCondition } from './evaluateCondition'

export function getVisibleQuestions(
  stage: StrategyQuestionnaireStage,
  answers: StrategyQuestionnaireAnswersMap
): Question[] {
  const questions = stage?.questions ?? []
  return getVisibleQuestionsFromList(questions, answers)
}

export function getVisibleQuestionsFromList(
  questions: Question[],
  answers: StrategyQuestionnaireAnswersMap
): Question[] {
  const list = questions ?? []
  return list.filter((question) =>
    evaluateCondition(question?.conditionalExpression ?? '', answers)
  )
}

export function getVisibleOptions(
  question: Question,
  answers: StrategyQuestionnaireAnswersMap
): QuestionOption[] {
  const options = question?.options ?? []
  return options.filter(
    (option) =>
      !(option?.deprecated ?? false) &&
      evaluateCondition(option?.conditionalExpression ?? '', answers)
  )
}

export function getResolvedQuestion(
  question: Question,
  answers: StrategyQuestionnaireAnswersMap
): Question {
  const visibleOptions = getVisibleOptions(question, answers)
  return { ...question, options: visibleOptions }
}

export interface NextVisibleQuestionResult {
  nextIndex: number
  nextQuestion: Question
}

/**
 * After an answer, find the next question index in the post-answer visible list.
 * Handles newly revealed questions when conditionals change the visible set.
 *
 * @param visibleBefore — visible questions before the answer
 * @param visibleAfter — visible questions after the answer
 * @param currentQuestionId — id of the question just answered
 * @param fallbackCurrentIndex — cursor index if current id not found in after-list
 */
export function findNextVisibleQuestionIndex(
  visibleBefore: Question[],
  visibleAfter: Question[],
  currentQuestionId: string | undefined,
  fallbackCurrentIndex: number
): NextVisibleQuestionResult | null {
  const currentIndexInVisible = visibleAfter.findIndex(
    (question) => question?.id === currentQuestionId
  )
  let nextIndex = currentIndexInVisible >= 0 ? currentIndexInVisible + 1 : fallbackCurrentIndex + 1
  let nextQuestion = visibleAfter[nextIndex] ?? null

  // Answer may reveal a new question inserted before the old "next" — jump to first new id
  if (nextQuestion == null) {
    const beforeIds = new Set(
      visibleBefore.map((question) => question?.id).filter(Boolean)
    )
    const firstNewlyVisibleIndex = visibleAfter.findIndex(
      (question) =>
        question?.id != null && !beforeIds.has(question.id)
    )
    if (firstNewlyVisibleIndex >= 0) {
      nextQuestion = visibleAfter[firstNewlyVisibleIndex]
      nextIndex = firstNewlyVisibleIndex
    }
  }

  return nextQuestion != null ? { nextIndex, nextQuestion } : null
}
